/**
 * ReportPageLayout - Enhetlig layout för rapportsidor
 * Ger konsekvent struktur: header, eventuellt meddelande, filter, innehåll
 */

import React from 'react';
import { PageHeader } from './PageHeader';

export interface ReportPageLayoutProps {
  /** Sidans titel */
  title: string;
  /** Valfri beskrivning under titeln */
  description?: string;
  /** Valfri ikon i rubriken */
  icon?: React.ReactNode;
  /** Valfria åtgärder (t.ex. export-knapp) */
  actions?: React.ReactNode;
  /** Egen bakåt-hanterare till PageHeader (ett steg i drill-down) */
  onBack?: () => void;
  /** Valfritt meddelande (t.ex. API-varning, info) */
  message?: React.ReactNode;
  /** Valfritt filterområde (t.ex. OrgTreeSelect + DateRangeFilter) */
  filters?: React.ReactNode;
  /** Huvudinnehåll */
  children: React.ReactNode;
  /** Alternativ padding ('normal' | 'compact') */
  padding?: 'normal' | 'compact';
  /** Filter-container: 'card' = bg-bgCard, 'elevated' = bg-bgElevated + border */
  filtersVariant?: 'card' | 'elevated';
  /** Bakgrund: 'default' = bg-bgDefault, 'container' = bg-bgContainer */
  variant?: 'default' | 'container';
  /** Min-höjd: 'screen' = min-h-screen, 'full' = min-h-full */
  minHeight?: 'screen' | 'full';
  /** Ytterligare wrapper-klasser */
  className?: string;
}

export const ReportPageLayout: React.FC<ReportPageLayoutProps> = ({
  title,
  description,
  icon,
  actions,
  onBack,
  message,
  filters,
  children,
  padding = 'normal',
  filtersVariant = 'card',
  variant = 'default',
  minHeight = 'screen',
  className = '',
}) => {
  const paddingClass = padding === 'compact' ? 'p-4' : 'p-6';
  const bgClass = variant === 'container' ? 'bg-bgContainer' : 'bg-bgDefault';
  const minHClass = minHeight === 'full' ? 'min-h-full' : 'min-h-screen';
  const filtersClass =
    filtersVariant === 'elevated'
      ? 'bg-bgElevated rounded-lg border border-border p-4 mb-6'
      : 'bg-bgCard rounded-lg p-4 mb-6 shadow-sm';

  return (
    <div className={`${paddingClass} ${bgClass} ${minHClass} ${className}`}>
      <PageHeader
        title={title}
        description={description}
        icon={icon}
        actions={actions}
        onBack={onBack}
      />

      {message && <div className="mb-6">{message}</div>}

      {filters && <div className={filtersClass}>{filters}</div>}

      {children}
    </div>
  );
};
